import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, AlertTriangle, FileText, ListChecks, Layers, History,
  CheckCircle2, XCircle, Activity, ClipboardList, UserCheck, ArrowLeft
} from 'lucide-react';
import OffLabelAttestationCard from '@/components/OffLabelAttestationCard';
import PhysicianAttestationBar from '@/components/PhysicianAttestationBar';
import ProblemListPanel from '@/components/ProblemListPanel';
import MoatsDownloadPanel from '@/components/MoatsDownloadPanel';
import SbarHandoffPanel from '@/components/SbarHandoffPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const SEV_COLORS = {
  CRITICAL: { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-800', tag: 'bg-red-600' },
  HIGH: { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-800', tag: 'bg-orange-600' },
  MODERATE: { bg: 'bg-amber-50', border: 'border-amber-500', text: 'text-amber-800', tag: 'bg-amber-600' },
  LOW: { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-800', tag: 'bg-blue-500' },
};

const TRIAGE_COLORS = {
  URGENT: 'bg-red-600 text-white',
  SEMI_URGENT: 'bg-orange-500 text-white',
  ROUTINE: 'bg-amber-500 text-white',
  PREVENTIVE: 'bg-emerald-600 text-white',
};

/**
 * PhysicianReviewPage — Item 7 of Triage Hardening Program (May 27, 2026)
 * Single-screen physician review surface. Pulls all audit data via the
 * /api/encounter/{id}/physician-review bundle endpoint. Read-only.
 */
export default function PhysicianReviewPage() {
  const { encounterId } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!encounterId) return;
    setLoading(true);
    axios.get(`${API}/api/encounter/${encounterId}/physician-review`)
      .then(r => { setBundle(r.data); setLoading(false); })
      .catch(e => {
        setError(e.response?.data?.error || e.message || 'Failed to load review bundle');
        setLoading(false);
      });
  }, [encounterId]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-600" data-testid="physician-review-loading">Loading physician review bundle…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6" data-testid="physician-review-error">
        <XCircle className="w-12 h-12 text-red-500 mb-3" />
        <div className="text-slate-800 font-bold">Unable to load review bundle</div>
        <div className="text-slate-600 text-sm mt-1">{error}</div>
        <Link to="/" className="mt-4 text-sm text-blue-600 underline">← Back to home</Link>
      </div>
    );
  }
  if (!bundle) return null;

  const triage = bundle.triage || {};
  const tcat = triage.triage_category || 'PREVENTIVE';

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="physician-review-page">
      {/* Header */}
      <header className="bg-slate-900 text-white border-b-4 border-blue-500">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-slate-300 hover:text-white text-xs flex items-center gap-1" data-testid="back-to-home-link">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
            <div className="w-px h-6 bg-slate-700" />
            <UserCheck className="w-5 h-5 text-blue-400" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">Physician Review · Read-Only · Audit Surface</div>
              <div className="text-sm font-semibold">Encounter <span className="font-mono text-emerald-300">{bundle.encounter_id}</span> · {bundle.patient_name || 'Anonymous patient'}</div>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-md text-sm font-bold uppercase tracking-wider ${TRIAGE_COLORS[tcat] || 'bg-slate-500 text-white'}`} data-testid="triage-category-badge">
            {tcat.replace('_', '-')}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-12 gap-6">
        {/* LEFT — Triage, Chief Complaint, Risk Flags, Prior Encounters */}
        <div className="col-span-12 lg:col-span-4 space-y-4">

          {/* Chief Complaint */}
          <section className="bg-white rounded-lg border-2 border-slate-200 p-4" data-testid="chief-complaint-card">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-slate-600" />
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Chief Complaint</h2>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">{bundle.chief_complaint || '—'}</p>
          </section>

          {/* Triage Rationale */}
          <section className="bg-white rounded-lg border-2 border-slate-200 p-4" data-testid="triage-rationale-card">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-slate-600" />
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Triage Decision</h2>
            </div>
            <div className="text-xs text-slate-500 mb-1">Rule applied: <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">{triage.rule_applied || '—'}</code></div>
            <div className="text-xs text-slate-500 mb-1">Triggering engine: <strong className="text-slate-800">{triage.triggering_engine || '—'}</strong></div>
            <div className="text-xs text-slate-500 mb-1">Triggering rule: <strong className="text-slate-800">{triage.triggering_rule || '—'}</strong></div>
            <div className="text-xs text-slate-500 mb-2">Severity: <strong className="text-slate-800">{triage.triggering_severity || '—'}</strong></div>
            <p className="text-sm text-slate-700 italic leading-relaxed mt-2 border-l-2 border-blue-400 pl-3">{triage.rationale || '—'}</p>
            <div className="mt-2 text-[10px] text-slate-400">Decision method: {triage.decision_method || '—'} · {triage.decision_at}</div>
          </section>

          {/* Risk Flags */}
          <section className="bg-white rounded-lg border-2 border-slate-200 p-4" data-testid="risk-flags-card">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Risk Flags ({bundle.risk_flags?.length || 0})</h2>
            </div>
            {!bundle.risk_flags?.length ? (
              <p className="text-xs text-slate-500 italic">No deterministic flags fired.</p>
            ) : (
              <ul className="space-y-2">
                {bundle.risk_flags.map((f, i) => {
                  const c = SEV_COLORS[f.severity] || SEV_COLORS.LOW;
                  return (
                    <li key={i} className={`${c.bg} ${c.border} border-l-4 rounded p-2`} data-testid={`risk-flag-${i}`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">{f.category}</span>
                        <span className={`text-[9px] font-bold text-white px-2 py-0.5 rounded ${c.tag}`}>{f.severity}</span>
                      </div>
                      <p className={`text-xs ${c.text}`}>{f.message}</p>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Prior Encounters */}
          <section className="bg-white rounded-lg border-2 border-slate-200 p-4" data-testid="prior-encounters-card">
            <div className="flex items-center gap-2 mb-2">
              <History className="w-4 h-4 text-slate-600" />
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Prior Encounters ({bundle.prior_encounters?.length || 0})</h2>
            </div>
            {!bundle.prior_encounters?.length ? (
              <p className="text-xs text-slate-500 italic">First encounter for this patient (or anonymous).</p>
            ) : (
              <ul className="space-y-1">
                {bundle.prior_encounters.map((p, i) => (
                  <li key={i} className="text-xs text-slate-700 border-b border-slate-100 pb-1" data-testid={`prior-encounter-${i}`}>
                    <span className="font-mono text-blue-600">{p.encounterId}</span>
                    {p.triageCategory && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">[{p.triageCategory}]</span>}
                    <div className="text-[10px] text-slate-500">{p.createdAt || p.visitDate || ''}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* CENTER — SOAP + Source Attribution */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          {/* FIX 1+2 (2026-06-11) — clinical vocabulary collision warning. Physician review only. */}
          {Array.isArray(bundle.transcription_warnings) && bundle.transcription_warnings.length > 0 && (
            <section className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4" data-testid="transcription-warning-card">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-700" />
                <div>
                  <h2 className="text-xs font-bold text-yellow-900 uppercase tracking-wider mb-1">Possible transcription error — please verify patient input.</h2>
                  <ul className="list-disc list-inside space-y-1">
                    {bundle.transcription_warnings.map((w, i) => (
                      <li key={i} className="text-[11px] text-yellow-900" data-testid={`transcription-warning-${i}`}>{w.detail || w.message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          )}
          {/* Moat 2 — Physician Attestation Bar (state machine: DRAFT/ATTESTED/AMENDED).
              Renders at the top so the encounter status is the first thing
              the physician sees. */}
          <PhysicianAttestationBar
            encounterId={bundle.encounter_id}
            initialAttestation={bundle.attestation}
          />
          {/* Problem List — persistent, patient-scoped health history,
              deterministically curated, distinct from this visit's complaint.
              Informational only — never feeds triage. */}
          <ProblemListPanel
            encounterId={bundle.encounter_id}
            initialProblemList={bundle.problem_list}
          />
          {/* Engine 68 — Off-Label Attestation card. Renders only when off-
              label drugs were detected. Surfaces independent of priority cap. */}
          <OffLabelAttestationCard
            encounterId={bundle.encounter_id}
            offlabelAttestations={bundle.offlabel_attestations}
          />
          {/* SBAR Hand-off — deterministic shift-change document.
              Founder GO 2026-06-08. Sits above the Moats panel because
              SBAR is the hand-off framework hospitalists live in at
              every shift change (WHO Patient Safety Solution #3). */}
          <SbarHandoffPanel encounterId={bundle.encounter_id} />
          {/* Legal Moats panel — surfaces applicable Moats B-H download buttons
              + the one-click Investor Demo Bundle. Built 2026-06-08. */}
          <MoatsDownloadPanel encounterId={bundle.encounter_id} />
          <section className="bg-white rounded-lg border-2 border-slate-200 p-5" data-testid="soap-card">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="w-5 h-5 text-slate-700" />
              <h2 className="text-base font-bold text-slate-900">SOAP Note</h2>
              <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-300 px-2 py-0.5 rounded font-bold ml-auto">Deterministic · 0 LLM contribution</span>
            </div>
            {['subjective', 'objective', 'assessment', 'plan'].map(section => {
              const text = bundle.soap?.[section] || '';
              const attr = bundle.soap_attribution?.[section];
              return (
                <div key={section} className="mb-3 pb-3 border-b border-slate-100 last:border-b-0" data-testid={`soap-section-${section}`}>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest">{section}</h3>
                    {attr && <span className="text-[9px] text-slate-500 italic">Source: {attr.source_class}</span>}
                  </div>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{text || <em className="text-slate-400">(none)</em>}</p>
                </div>
              );
            })}
            {bundle.soap_attribution?.audit_summary && (
              <div className="mt-3 pt-3 border-t-2 border-slate-200 text-[11px] text-slate-600 italic bg-slate-50 rounded p-2">
                <strong className="text-slate-800 not-italic">Attribution audit:</strong> {bundle.soap_attribution.audit_summary}
              </div>
            )}
          </section>
        </div>

        {/* RIGHT — Rule Trace, Aggregation, Identity, Firewall */}
        <div className="col-span-12 lg:col-span-3 space-y-4">

          {/* Rule Trace */}
          <section className="bg-white rounded-lg border-2 border-slate-200 p-4" data-testid="rule-trace-card">
            <div className="flex items-center gap-2 mb-2">
              <ListChecks className="w-4 h-4 text-slate-600" />
              <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Rule Trace ({bundle.rule_trace?.length || 0})</h2>
            </div>
            {!bundle.rule_trace?.length ? (
              <p className="text-[11px] text-slate-500 italic">No traces.</p>
            ) : (
              <ul className="space-y-1.5">
                {bundle.rule_trace.slice(0, 12).map((t, i) => (
                  <li key={i} className="text-[10px] border-b border-slate-100 pb-1" data-testid={`rule-trace-${i}`}>
                    <div className="font-mono text-slate-700 truncate" title={t.engine}>{t.engine}</div>
                    <div className="text-slate-600 truncate" title={t.rule}>{t.rule}</div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-[9px] uppercase font-bold text-slate-500">{t.severity}</span>
                      <span className="text-[9px] text-emerald-700">{t.source}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Aggregation Trace */}
          <section className="bg-white rounded-lg border-2 border-slate-200 p-4" data-testid="aggregation-trace-card">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-slate-600" />
              <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Aggregation</h2>
            </div>
            {!bundle.aggregation_trace ? (
              <p className="text-[11px] text-slate-500 italic">No trace.</p>
            ) : (
              <>
                <div className="text-[10px] text-slate-600 mb-1">Final flag count: <strong>{bundle.aggregation_trace.final_flag_count}</strong></div>
                <div className="text-[10px] text-slate-600 mb-2">Distribution:</div>
                <div className="space-y-0.5">
                  {Object.entries(bundle.aggregation_trace.severity_distribution || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-[10px]">
                      <span className={`font-bold ${SEV_COLORS[k]?.text || 'text-slate-700'}`}>{k}</span>
                      <span className="text-slate-800">{v}</span>
                    </div>
                  ))}
                </div>
                {bundle.aggregation_trace.conflict_resolutions?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <div className="text-[10px] text-slate-600 font-bold mb-1">{bundle.aggregation_trace.conflict_resolutions.length} resolution(s)</div>
                    {bundle.aggregation_trace.conflict_resolutions.slice(0, 3).map((c, i) => (
                      <div key={i} className="text-[9px] text-slate-600 mb-1">
                        <strong>{c.category}:</strong> {c.action_taken}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>

          {/* Identity Audit */}
          <section className="bg-white rounded-lg border-2 border-slate-200 p-4" data-testid="identity-audit-card">
            <div className="flex items-center gap-2 mb-2">
              <UserCheck className="w-4 h-4 text-slate-600" />
              <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Identity</h2>
            </div>
            {!bundle.identity_audit ? (
              <p className="text-[11px] text-slate-500 italic">No audit.</p>
            ) : (
              <>
                <div className="text-[10px] text-slate-600 mb-1">Quality: <strong className={bundle.identity_audit.identity_quality === 'STRONG' ? 'text-emerald-700' : (bundle.identity_audit.identity_quality === 'WEAK' ? 'text-amber-700' : 'text-slate-700')}>{bundle.identity_audit.identity_quality}</strong></div>
                <div className="text-[10px] text-slate-600 mb-1">Outcome: <strong>{bundle.identity_audit.resolution_outcome}</strong></div>
                <div className="text-[10px] text-slate-600 mb-1">Prior visits: {bundle.identity_audit.prior_encounter_count}</div>
                <div className="text-[10px] text-emerald-700 mt-2 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> No-merge confirmed</div>
              </>
            )}
          </section>

          {/* Firewall Status */}
          <section className={`rounded-lg border-2 p-4 ${(bundle.firewall_violations?.length || 0) === 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-400'}`} data-testid="firewall-status-card">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className={`w-4 h-4 ${(bundle.firewall_violations?.length || 0) === 0 ? 'text-emerald-700' : 'text-red-700'}`} />
              <h2 className={`text-xs font-bold uppercase tracking-wider ${(bundle.firewall_violations?.length || 0) === 0 ? 'text-emerald-800' : 'text-red-800'}`}>Ambient Firewall</h2>
            </div>
            <div className="text-[10px]">
              {(bundle.firewall_violations?.length || 0) === 0 ? (
                <span className="text-emerald-700 font-semibold">0 violations — all flags from deterministic engines</span>
              ) : (
                <span className="text-red-700 font-bold">{bundle.firewall_violations.length} violations blocked!</span>
              )}
            </div>
          </section>

          {/* Missing Questions (Item 8 — advisory only) */}
          {bundle.missing_questions?.missing_questions?.length > 0 && (
            <section className="bg-white rounded-lg border-2 border-purple-300 p-4" data-testid="missing-questions-card">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-purple-600" />
                <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Missing Questions ({bundle.missing_questions.missing_questions.length})</h2>
              </div>
              <p className="text-[9px] text-slate-500 italic mb-2">Advisory — physician review only. Not clinical decisions.</p>
              <ul className="space-y-1">
                {bundle.missing_questions.missing_questions.slice(0, 8).map((q, i) => (
                  <li key={i} className="text-[11px] text-slate-700 border-l-2 border-purple-400 pl-2 py-0.5" data-testid={`missing-question-${i}`}>
                    {q.question}
                    <span className="block text-[9px] text-purple-700 italic">[{q.category}]</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Longitudinal Diff (Item 9) */}
          {bundle.longitudinal_diff && (
            <section className="bg-white rounded-lg border-2 border-blue-300 p-4" data-testid="longitudinal-diff-card">
              <div className="flex items-center gap-2 mb-2">
                <History className="w-4 h-4 text-blue-600" />
                <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Longitudinal Diff</h2>
              </div>
              <div className="text-[10px] text-slate-600 space-y-1">
                <div>vs {bundle.longitudinal_diff.compared_against_count} prior encounter(s)</div>
                {bundle.longitudinal_diff.worsening_flags?.length > 0 && (
                  <div className="text-red-700"><strong>{bundle.longitudinal_diff.worsening_flags.length}</strong> worsening</div>
                )}
                {bundle.longitudinal_diff.improving_flags?.length > 0 && (
                  <div className="text-emerald-700"><strong>{bundle.longitudinal_diff.improving_flags.length}</strong> improving</div>
                )}
                {bundle.longitudinal_diff.new_flags?.length > 0 && (
                  <div className="text-amber-700"><strong>{bundle.longitudinal_diff.new_flags.length}</strong> new</div>
                )}
                {bundle.longitudinal_diff.resolved_flags?.length > 0 && (
                  <div className="text-blue-700"><strong>{bundle.longitudinal_diff.resolved_flags.length}</strong> resolved</div>
                )}
                {bundle.longitudinal_diff.medications_added?.length > 0 && (
                  <div className="pt-1 border-t border-slate-100">Meds added: {bundle.longitudinal_diff.medications_added.slice(0, 3).join(', ')}</div>
                )}
              </div>
            </section>
          )}
        </div>
      </main>

      <footer className="max-w-7xl mx-auto px-6 mt-6 text-center text-[10px] text-slate-400 italic">
        Bundle generated {bundle.bundled_at} · Version {bundle.bundle_version} · Read-only physician review surface · P.Ai Global Health · Patent Pending
      </footer>
    </div>
  );
}
